import { useState, useEffect } from 'react';
import { fetchPricingData, PricingData } from '@/services/pricingService';
import * as Sentry from '@sentry/react';

/**
 * Hook to fetch pricing data from Firebase
 */
export const usePricingData = () => {
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getPlanName = (planId: string): string | undefined => {
    if (!pricingData) {
      return undefined;
    }
    return Object.values(pricingData).find((p) => p.product_id === planId)?.name;
  };

  useEffect(() => {
    const loadPricingData = async () => {
      try {
        setLoading(true);
        setError(null);

        const firebasePricing = await fetchPricingData();
        if (firebasePricing) {
          setPricingData(firebasePricing);
        } else {
          setError('No pricing data found');
        }
      } catch (err) {
        Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
          tags: { feature: 'pricing-data' },
          extra: { context: 'loading pricing data', rawError: err instanceof Error ? undefined : err },
        });
        setError('Failed to load pricing data');
      } finally {
        setLoading(false);
      }
    };

    loadPricingData();
  }, []);

  return {
    getPlanName,
    pricingData,
    loading,
    error,
    refresh: () => {
      const loadPricingData = async () => {
        try {
          setLoading(true);
          setError(null);
          const firebasePricing = await fetchPricingData();
          if (firebasePricing) {
            setPricingData(firebasePricing);
          }
        } catch (err) {
          Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
            tags: { feature: 'pricing-data' },
            extra: { context: 'refreshing pricing data', rawError: err instanceof Error ? undefined : err },
          });
          setError('Failed to refresh pricing data');
        } finally {
          setLoading(false);
        }
      };
      loadPricingData();
    },
  };
};
